function finite(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function normalizeMoboFormFactor(value) {
  const raw = String(value || '').trim().toLowerCase()
    .replace(/micro[-_ ]?atx|m[-_ ]?atx/g, 'matx')
    .replace(/mini[-_ ]?itx/g, 'itx');
  if (raw.includes('matx')) return 'MATX';
  if (raw.includes('dtx')) return 'DTX';
  if (raw.includes('atx') && !raw.includes('itx')) return 'ATX';
  if (raw.includes('itx')) return 'ITX';
  return '';
}

export function normalizePsuFormFactor(value) {
  const raw = String(value || '').trim().toLowerCase().replace(/\s+/g, '').replace(/-/g, '_');
  if (raw.includes('sfx_l') || raw.includes('sfxl')) return 'SFX-L';
  if (raw.includes('flex')) return 'FLEX';
  if (raw === 'atx' || (raw.includes('atx') && !raw.includes('sfx'))) return 'ATX';
  if (raw.includes('sfx')) return 'SFX';
  return '';
}

function statePool(plan, caseState = null) {
  const effective = plan?.effective || {};
  const profile = plan?.capabilityProfile || {};
  const activeModeId = String(effective?.activeMode?.mode_id || effective?.activeMode?.id || caseState?.modeId || 'default');
  const fixed = !!effective?.fixedStep || !!caseState?.fixedStep;

  if (fixed) return [{ id:'active', limits:effective.limits || {} }];

  if (activeModeId && activeModeId !== 'default') {
    const modeStates = (profile.states || []).filter(s => String(s.modeId || '') === activeModeId);
    if (modeStates.length) return modeStates;
    return [{ id:'active-mode', limits:effective.limits || {} }];
  }

  return (profile.states && profile.states.length)
    ? profile.states
    : [{ id:'effective', limits:effective.limits || {} }];
}

function fitsGpuState(dims, limits, meta = {}) {
  const l=finite(dims?.l), t=finite(dims?.w), h=finite(dims?.h);
  const maxL=finite(limits?.maxGPU), maxT=finite(limits?.maxGPUT ?? limits?.maxThick), maxH=finite(limits?.maxGPUH);
  if (maxL > 0 && l > maxL + 0.01) return false;
  if (maxT > 0 && t > maxT + 0.01) return false;
  if (maxH > 0 && h > maxH + 0.01) return false;
  return true;
}

function fitsCoolerState(dims, limits) {
  const h=finite(dims?.h), max=finite(limits?.maxCPU);
  return !(max > 0 && h > max + 0.01);
}

function fitsRadiatorState(meta, dims, limits) {
  const nominal=finite(meta?.radiatorSize) || Math.round(finite(dims?.l) / 40) * 40;
  const max=finite(limits?.maxAIO);
  if (max <= 0) return false;
  return !(nominal > 0 && nominal > max + 0.01);
}

function describeGpuFailure(dims, states, meta = {}) {
  const l=finite(dims?.l), t=finite(dims?.w), h=finite(dims?.h);
  const maxima = states.reduce((acc,s) => {
    const lim=s?.limits || {};
    acc.l=Math.max(acc.l,finite(lim.maxGPU));
    acc.t=Math.max(acc.t,finite(lim.maxGPUT ?? lim.maxThick));
    acc.h=Math.max(acc.h,finite(lim.maxGPUH));
    return acc;
  },{l:0,t:0,h:0});
  const parts=[];
  if(maxima.l>0 && l>maxima.l)parts.push(`length ${l.toFixed(1)}>${maxima.l.toFixed(1)}`);
  if(maxima.t>0 && t>maxima.t)parts.push(`thickness ${t.toFixed(1)}>${maxima.t.toFixed(1)}`);
  if(maxima.h>0 && h>maxima.h)parts.push(`height ${h.toFixed(1)}>${maxima.h.toFixed(1)}`);
  return parts.length ? `GPU ${parts.join(', ')}` : 'GPU has no compatible case state';
}

export function evaluateHardwareEligibility({ trackType, dims = {}, meta = {}, plan = null, caseState = null } = {}) {
  if (!plan) return { allowed:true, reason:'no_active_case' };
  const profile=plan.capabilityProfile || {};

  if (trackType === 'motherboard') {
    const ff=normalizeMoboFormFactor(meta.formFactor);
    const supported=(profile.motherboardTypes || []).map(normalizeMoboFormFactor).filter(Boolean);
    if (profile.motherboardSupportKnown && ff && supported.length && !supported.includes(ff)) {
      return { allowed:false, reason:`${ff} motherboard unsupported`, code:'unsupported_motherboard' };
    }
    // Replacement UI must not deadlock on the CURRENT build. A supported motherboard can
    // legitimately force the PSU/cooling/GPU into a different branch; that is solved after
    // the swap transaction. Surface branch trouble as advisory data instead of disabling it.
    const branches=(plan.configurationBranches || []).filter(branch=>String(branch?.mobo||'').toUpperCase()===ff);
    const branchCompatible=!branches.length || branches.some(branch=>branch.valid!==false);
    const detail=branchCompatible ? null : branches.flatMap(branch=>branch.failures||[]).filter(Boolean)[0] || `${ff} motherboard needs a different active configuration`;
    return { allowed:true, reason:'supported_motherboard', branchCompatible, advisory:detail };
  }

  if (trackType === 'psu') {
    const ff=normalizePsuFormFactor(meta.formFactor);
    const supported=(profile.psuTypes || []).map(normalizePsuFormFactor).filter(Boolean);
    if (profile.psuSupportKnown && ff && supported.length && !supported.includes(ff)) {
      return { allowed:false, reason:`${ff} PSU unsupported`, code:'unsupported_psu' };
    }
    // As with motherboard replacement, branch validity belongs to the post-swap solve.
    // Do not prevent selecting a case-supported PSU because the old board/cooling branch
    // cannot coexist with it.
    const branches=(plan.configurationBranches || []).filter(branch=>String(branch?.psu||'').toUpperCase()===ff);
    const branchCompatible=!branches.length || branches.some(branch=>branch.valid!==false);
    const detail=branchCompatible ? null : branches.flatMap(branch=>branch.failures||[]).filter(Boolean)[0] || `${ff} PSU needs a different active configuration`;
    return { allowed:true, reason:'supported_psu', branchCompatible, advisory:detail };
  }

  const states=statePool(plan,caseState);
  if (trackType === 'gpu') {
    const ok=states.some(s=>fitsGpuState(dims,s.limits || {},meta));
    return ok ? { allowed:true, reason:'gpu_supported_in_case_pool' }
      : { allowed:false, reason:describeGpuFailure(dims,states,meta), code:'gpu_clear_fail' };
  }
  if (trackType === 'aircooler') {
    const ok=states.some(s=>fitsCoolerState(dims,s.limits || {}));
    return ok ? { allowed:true, reason:'cooler_supported_in_case_pool' }
      : { allowed:false, reason:'CPU cooler exceeds every available case state', code:'cooler_clear_fail' };
  }
  if (trackType === 'radiator') {
    const ok=states.some(s=>fitsRadiatorState(meta,dims,s.limits || {}));
    return ok ? { allowed:true, reason:'radiator_supported_in_case_pool' }
      : { allowed:false, reason:`${finite(meta?.radiatorSize)||'AIO'} radiator unsupported`, code:'aio_clear_fail' };
  }
  return { allowed:true, reason:'not_case_gated' };
}
